import { isFalsyButNotZero, isLetters } from "../../../../utils/Validations";
import getVersionedFields from "..";
import { closingCostDropDownValues, dscrArmIndexValues, dscrArmTypeValues, dscrRateTypeDropDownValues, prepaymentPenaltyMonths, prepaymentPenaltyTypeDropDownValues, rateTypeDropDownValues } from "../../v1/LoanFieldsData";
import { getCookie } from "../../../../utils/Common";
import { Roles } from "../../../../utils/enums";

const isCreditCommitteeViewer = () => {
  const userPermissions = JSON.parse(getCookie("userPermissions") || "[]");
  return userPermissions.includes(Roles.creditCommittee);
};

// update_pricing is the source of truth for editing Interest Rate / Final
// Toorak Price: a user with it can see and edit these fields regardless of
// job role, while a user without it still sees whatever the existing
// isCreditCommitteeViewer visibility rule already granted them, but cannot
// edit — isDisabled (checked independently of isVisible by the field
// renderers) is what actually enforces that.
const hasUpdatePricing = () => {
  const userPermissions = JSON.parse(getCookie("userPermissions") || "[]");
  return userPermissions.includes(Roles.updatePricing);
};

const InterestOnlyFlag = {
  // TODO need to check with Moukthik on field mapping
  id: "interestOnlyFlag",
  label: "interestOnlyFlag",
  required: true,
  type: (loanType: string, dataView: string) => "buttonGroup",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  minimumDataView: true,
  data: [
    { label: "yes", value: "Yes" },
    { label: "no", value: "No" },
  ],
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  fieldInfo: "Select 'Yes' if the loan will have an interest-only period, where the borrower pays only the interest for a specified duration.",
};

const ioPeriod = {
  id: "ioPeriod",
  label: "ioPeriod",
  required: true,
  type: (loanType: string, dataView: string) => "number",
  getFieldErrors: (value: any) =>
    isNaN(+value) || isLetters(value)
      ? "enterValidNumber"
      : !value.trim().length || value.includes(".")
        ? "validValue"
        : value < 0 ? "cannotBeNegative" : "",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.interestOnlyFlag === "Yes",
  isExpandedView: false,
  isDisabled: true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  fieldInfo: "",
};

const PrePaymentPenaltyTypeMonths = {
  id: "prepaymentPenaltyTypeMonths",
  label: "prepaymentPenaltyMonths",
  required: true,
  type: (loanType: string, dataView: string) => "slider",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  data: prepaymentPenaltyMonths,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  fieldInfo: "Select the prepayment penalty months. This indicates the penalty schedule for early repayment.",
};

const BorrowerRequested = {
  id: "borrowerRequested",
  label: "loanAmount",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : value < 0 ? "cannotBeNegative" : "",
  minimumDataView: true,
  columnSplit: 6,
  isCurrency: true,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isExpandedView: false,
  fieldInfo: "Enter the total amount being borrowed for the loan."
};

const PrePaymentPenaltyType = {
  id: "prepayPenaltyType",
  label: "prepayPenaltyType",
  required: true,
  type: (loanType: string, dataView: string) => "dropDown",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  data: prepaymentPenaltyTypeDropDownValues,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isExpandedView: false,
  fieldInfo: "Select the prepayment penalty structure for the loan.",
};

const rateType = {
  id: "rateType",
  label: "rateType",
  required: true,
  type: (loanType: string, dataView: string) => "dropDown",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  minimumDataView: false,
  data: dscrRateTypeDropDownValues,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  fieldInfo: "Choose the type of interest rate for the loan, such as fixed or adjustable.",
};

const exitDebt = {
  id: "exitDebt",
  label: "exitDebt",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  required: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
};

const hardCosts: any = {
  id: "hardCosts",
  label: "hardCosts",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  required: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
};

const softCostsCap15Rehab = {
  id: "softCostsCap15Rehab",
  label: "softCostsCap15Rehab",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
};

const TotalRehabBudget = {
  id: "totalRehabBudget",
  label: "totalRehabBudget",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  columnSplit: 6,
  isCurrency: true,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  popUpMessage: "RehabNotAllowed",
};

const InitialLoanAmount = {
  id: "initialLoanAmount",
  label: "initialLoanAmount",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  columnSplit: 6,
  isCurrency: true,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isExpandedView: false,
};

const TotalOrgAndDiscountPoints = {
  id: "totalOrgAndDiscountPoints",
  label: "totalOrgAndDiscountPoints",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const interestRate = {
  id: "interestRate",
  label: "interestRate",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

// Credit-Committee-only manual pricing entry (DSCR). Writes the same
// `loanEconomics.interestRate` data key as the Bridge-only `interestRate`
// field above, but is only visible to Credit Committee, and only wired
// into the DSCR field list - the Bridge field above is untouched.
const CreditCommitteeInterestRate = {
  id: "interestRate",
  label: "interestRate",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  columnSplit: 6,
  isVisible: () => isCreditCommitteeViewer() || hasUpdatePricing(),
  isDisabled: () => !hasUpdatePricing(),
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  // loanEconomics.interestRate is also written automatically by buydown
  // selection and every guideline/partner evaluation (see
  // BuyDownOptionsWrapper.tsx) — without this, the CC-only override input
  // would show those auto-computed values as if someone had typed a manual
  // override. pricingOverrideFlags.interestRate is only ever set true by a
  // real keystroke into this field (applyGeneralSectionChange), so the input
  // stays blank otherwise even though the underlying value is populated.
  getDisplayValue: (currentObjValue: any) =>
    currentObjValue?.pricingOverrideFlags?.interestRate
      ? currentObjValue?.interestRate ?? ""
      : "",
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const getFinalToorakPriceFieldError = (value: string): string => {
  if (!isFalsyButNotZero(value)) return "thisFieldIsRequired";
  if (isLetters(value)) return "validValue";
  if (+value < 0) return "cannotBeNegative";
  return "";
};

const FinalToorakPrice = {
  id: "finalToorakPrice",
  label: "finalToorakPrice",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  columnSplit: 6,
  isVisible: () => isCreditCommitteeViewer() || hasUpdatePricing(),
  isDisabled: () => !hasUpdatePricing(),
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  // See CreditCommitteeInterestRate's getDisplayValue comment above — same
  // reasoning, separate flag.
  getDisplayValue: (currentObjValue: any) =>
    currentObjValue?.pricingOverrideFlags?.finalToorakPrice
      ? currentObjValue?.finalToorakPrice ?? ""
      : "",
  getFieldErrors: getFinalToorakPriceFieldError,
};

const financedBudget: any = {
  id: "financedBudget",
  label: "financedBudget",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  required: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
  isExpandedView: false,
  popUpMessage: "budgetMismatch",
  showPopUpMessage: (data: any) => {
    const value1 = +data?.financedBudget 
    let value2 =  +(data?.totalRehabBudget || data?.totalBudget)
    let showPopup = +value1 > +value2

    if(showPopup && (!value2 || isNaN(value2))) {
      value2 = +(data?.softCosts ?? data?.softCostsCap15Rehab ?? 0)  + (+data?.hardCosts )
      showPopup = +value1 > +value2
    }
    return showPopup
  }
};

const includeOutOfPocketBudgetARLTV = {
  id: "includeOutOfPocketBudgetARLTV",
  label: "includeOutOfPocketBudgetARLTV",
  required: true,
  type: (loanType: string, dataView: string) => "checkBox",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  columnSplit: 6,
  isVisible: () => false,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  disableField: true,
};

const armType = {
  id: "armType",
  label: "armType",
  required: true,
  type: () => "dropDown",
  data: dscrArmTypeValues || [],
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  isAutoCalculated: (loanType: string, dataView?: string) => false,
};

const armIndex = {
  id: "armIndex",
  label: "armIndex",
  required: true,
  type: () => "dropDown",
  data: dscrArmIndexValues || [],
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  isAutoCalculated: (loanType: string, dataView?: string) => false,
};

const grossArmMargin = {
  id: "grossArmMargin",
  label: "grossArmMargin",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const initialRateAdjustmentDate = {
  id: "initialRateAdjustmentDate",
  label: "initialRateAdjustmentDate",
  required: false,
  type: (loanType: string, dataView: string) => "date",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => false,
};

const initialPayAdjustmentDate = {
  id: "initialPayAdjustmentDate",
  label: "initialPayAdjustmentDate",
  required: false,
  type: (loanType: string, dataView: string) => "date",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value) ? "validValue" : "",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => false,
};

const initialPeriodicCap = {
  id: "initialPeriodicCap",
  label: "initialPeriodicCap",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const subsequentPeriodicCap = {
  id: "subsequentPeriodicCap",
  label: "subsequentPeriodicCap",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const lifeTimeCap = {
  id: "lifeTimeCap",
  label: "lifeTimeCap",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const lifeTimeFloor = {
  id: "lifeTimeFloor",
  label: "lifeTimeFloor",
  required: false,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: false,
  columnSplit: 6,
  isVisible: (data: any) => data?.rateType === "Adjustable",
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const OrgInterestRate = {
  id: "originalInterestRate",
  label: "originalInterestRate",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
  isExpandedView: false,
};

const softCosts: any = {
  id: "softCosts",
  label: "softCosts",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  required: true,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
};

const orgBrokerFee: any = {
  id: "orgBrokerFee",
  label: "orgBrokerFee",
  type: (loanType: string, dataView: string) => "dropDown",
  required: true,
  data: closingCostDropDownValues,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  getFieldErrors: (value: string) =>
    !isFalsyButNotZero(value)
      ? isLetters(value)
        ? "validValue"
        : "thisFieldIsRequired"
      : +value < 0 || +value > 100
      ? "percentageValueError"
      : "",
};

const InterestReserve = {
  id: "interestReserve",
  label: "interestReserve",
  required: true,
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isCurrency: true,
};

const totalBudget: any = {
  id: "totalBudget",
  label: "totalBudget",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : "",
  required: false,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => true,
  isCurrency: true,
  isExpandedView: false,
};

const originationFee = {
  id: "originationFee",
  label: "originationFee",
  type: (loanType: string, dataView: string) => "string",
  getFieldErrors: (value: any) =>
    !isFalsyButNotZero(value)
      ? "validValue"
      : value < 0
      ? "cannotBeNegative"
      : value > 100
      ? "percentageValueError"
      : "",
  required: true,
  minimumDataView: true,
  columnSplit: 6,
  isVisible: () => true,
  isAutoCalculated: (loanType: string, dataView?: string) => false,
  isPercentage: true,
  isExpandedView: false,
};

const loanEconomicsFields: any = {
  InterestOnlyFlag,
  ioPeriod,
  PrePaymentPenaltyTypeMonths,
  BorrowerRequested,
  PrePaymentPenaltyType,
  rateType,
  exitDebt,
  hardCosts,
  softCostsCap15Rehab,
  TotalRehabBudget,
  InitialLoanAmount,
  TotalOrgAndDiscountPoints,
  interestRate,
  CreditCommitteeInterestRate,
  FinalToorakPrice,
  financedBudget,
  includeOutOfPocketBudgetARLTV,
  armType,
  armIndex,
  grossArmMargin,
  initialRateAdjustmentDate,
  initialPayAdjustmentDate,
  initialPeriodicCap,
  subsequentPeriodicCap,
  lifeTimeCap,
  lifeTimeFloor,
  OrgInterestRate,
  softCosts,
  InterestReserve,
  orgBrokerFee,
  totalBudget,
  originationFee
};

export const getLoanEconomicsFields = (
  productType: string,
  client: string,
  section: string,
  sizingType?: string
) => {
  const finalConfigToBeReturned: any = [];
  const vFields: any = getVersionedFields(productType);
  let configToBeReturned: any = vFields?.[client]?.["inputFields"]?.[section];

  if (sizingType) {
    configToBeReturned = configToBeReturned?.[sizingType];
  };

  configToBeReturned?.map((field: string) => {
    if (loanEconomicsFields[field]) {
      finalConfigToBeReturned.push(loanEconomicsFields[field]);
    }
  });

  return finalConfigToBeReturned;
};
